/**
 * UIContextExtractor – derives a structured snapshot of the current screen
 * from the URL + DOM. Used by:
 *   - ExplainEngine          → "Explain this screen"
 *   - RecommendationEngine   → "Next best actions"
 *
 * Strategy:
 *   - URL pattern → pageType
 *   - data-* attributes → KPIs, table summary, entity, workflow
 *   - Auth context → role
 *
 * The extractor is intentionally side-effect-free and DOM-read-only. It
 * does NOT issue API calls — it consumes already-rendered data so the
 * panels feel instant.
 */

export type PageType =
  | 'Dashboard'
  | 'PatientList'
  | 'Patient360'
  | 'CaseDetail'
  | 'Workflow'
  | 'Reports'
  | 'FormBuilder'
  | 'AssistanceList'
  | 'HCPList'
  | 'HospitalList'
  | 'Settings'
  | 'Unknown';

export interface UIKpi {
  key: string;
  label: string;
  value: string | number;
  trend?: 'up' | 'down' | 'flat';
}

export interface UITableSummary {
  totalRecords: number;
  visibleColumns: string[];
  hasFilters: boolean;
}

export interface UIEntitySummary {
  id?: string;
  name?: string;
  type?: string;
  /** Active/Open case count when known (Patient360 only). */
  cases?: number;
  approvedAmount?: string | number;
  utilizedAmount?: string | number;
  status?: string;
}

export interface UIWorkflowSummary {
  currentStep?: string;
  pendingTasks: number;
  slaBreached: number;
}

export interface UIContext {
  pageType: PageType;
  title: string;
  pathname: string;
  programId?: string;
  patientId?: string;
  caseId?: string;
  role?: string;
  kpis: UIKpi[];
  tableSummary?: UITableSummary;
  entity?: UIEntitySummary;
  workflow: UIWorkflowSummary;
  pendingTasks: number;
  alerts: string[];
  visibleSections: string[];
  filters: string[];
}

// ──────────────────────────────────────────────────────────────────────
// URL classification
// ──────────────────────────────────────────────────────────────────────

const GUID_RE = /[0-9a-fA-F]{36}/;

function classifyPage(pathname: string): { pageType: PageType; title: string } {
  const p = pathname.toLowerCase();

  if (/\/programs\/[^/]+\/(workspace|dashboard)\/?$/.test(p)) {
    return { pageType: 'Dashboard', title: 'Program Dashboard' };
  }
  if (/\/patients\/[^/]+(?:\/.*)?$/.test(p) && GUID_RE.test(p.split('/patients/')[1] ?? '')) {
    return { pageType: 'Patient360', title: 'Patient Details' };
  }
  if (/\/patients\/?$/.test(p) || /workspace\/patients/.test(p)) {
    return { pageType: 'PatientList', title: 'Patient List' };
  }
  if (/\/cases?\/[^/]+/.test(p)) {
    return { pageType: 'CaseDetail', title: 'Case Detail' };
  }
  if (/\/assistance/.test(p)) {
    return { pageType: 'AssistanceList', title: 'Assistance Cases' };
  }
  if (/\/workflow/.test(p) || /\/tasks/.test(p) || /\/approvals/.test(p)) {
    return { pageType: 'Workflow', title: 'Workflow Tasks' };
  }
  if (/\/reports/.test(p)) {
    return { pageType: 'Reports', title: 'Reports' };
  }
  if (/\/form-builder/.test(p) || /\/forms\//.test(p)) {
    return { pageType: 'FormBuilder', title: 'Form Builder' };
  }
  if (/\/hcps?/.test(p)) {
    return { pageType: 'HCPList', title: 'HCPs' };
  }
  if (/\/hospitals?/.test(p)) {
    return { pageType: 'HospitalList', title: 'Hospitals' };
  }
  if (/\/settings|\/superadmin|\/application-settings/.test(p)) {
    return { pageType: 'Settings', title: 'Settings' };
  }

  return { pageType: 'Unknown', title: 'Screen Overview' };
}

function extractIdFromPath(pathname: string, segment: string): string | undefined {
  const re = new RegExp(`/${segment}/([0-9a-fA-F-]{36})`, 'i');
  return pathname.match(re)?.[1];
}

// ──────────────────────────────────────────────────────────────────────
// DOM scanners
// ──────────────────────────────────────────────────────────────────────

function safeText(el: Element | null): string {
  return (el?.textContent ?? '').replace(/\s+/g, ' ').trim();
}

function parseNumeric(text: string): number | string {
  const cleaned = text.replace(/[, ]/g, '');
  const m = cleaned.match(/-?\d+(\.\d+)?/);
  if (!m) return text;
  const num = Number(m[0]);
  return Number.isFinite(num) ? num : text;
}

/**
 * Scans the DOM for KPI cards. Recognises (in priority order):
 *   1. [data-kpi-key] / [data-kpi-label] / [data-kpi-value]
 *   2. Elements inside [data-demo$="kpi-strip"] or [data-kpi-strip]
 *   3. Generic .kpi-card / [data-testid*="kpi"]
 */
function extractKpis(): UIKpi[] {
  const out: UIKpi[] = [];
  const seen = new Set<string>();

  const explicit = document.querySelectorAll<HTMLElement>('[data-kpi-key]');
  explicit.forEach((el) => {
    const key = el.dataset.kpiKey ?? '';
    if (!key || seen.has(key)) return;
    const label =
      el.dataset.kpiLabel ??
      safeText(el.querySelector('[data-kpi-label]')) ??
      safeText(el.querySelector('.kpi-label')) ??
      key;
    const valueText =
      el.dataset.kpiValue ??
      safeText(el.querySelector('[data-kpi-value]')) ??
      safeText(el.querySelector('.kpi-value')) ??
      '';
    out.push({ key, label, value: parseNumeric(valueText) });
    seen.add(key);
  });

  if (out.length > 0) return out;

  // Fallback – generic kpi card scan inside any kpi-strip wrapper.
  const wrappers = document.querySelectorAll<HTMLElement>(
    '[data-demo$="kpi-strip"], [data-kpi-strip], [data-testid="kpi-strip"]',
  );
  wrappers.forEach((wrap, wIdx) => {
    const cards = wrap.querySelectorAll<HTMLElement>(
      '[data-testid*="kpi"], .kpi-card, [data-kpi]',
    );
    cards.forEach((card, idx) => {
      const label = safeText(
        card.querySelector('[data-kpi-label], .kpi-label, .text-muted-foreground'),
      );
      const value = safeText(
        card.querySelector('[data-kpi-value], .kpi-value, .text-2xl, .text-xl, .font-semibold'),
      );
      if (!label && !value) return;
      const key = `kpi-${wIdx}-${idx}`;
      if (seen.has(key)) return;
      out.push({ key, label: label || key, value: parseNumeric(value) });
      seen.add(key);
    });
  });

  return out;
}

function extractTableSummary(): UITableSummary | undefined {
  const tables = document.querySelectorAll<HTMLElement>(
    '[data-demo$="table"], [data-demo$="-table"], table[data-table], [role="table"]',
  );
  if (tables.length === 0) return undefined;

  const tbl = tables[0];
  const rows = tbl.querySelectorAll('tbody tr, [role="row"]:not([data-header])').length;
  const headers = Array.from(
    tbl.querySelectorAll<HTMLElement>('thead th, [role="columnheader"]'),
  )
    .map((h) => safeText(h))
    .filter(Boolean);

  // Best-effort total: read [data-total-records] / pagination text first.
  const totalAttr =
    tbl.getAttribute('data-total-records') ??
    document.querySelector('[data-total-records]')?.getAttribute('data-total-records');
  const total = totalAttr ? Number(totalAttr) : rows;

  const filterCount = document.querySelectorAll<HTMLElement>(
    '[data-active-filter], [data-filter-chip]',
  ).length;

  return {
    totalRecords: Number.isFinite(total) ? total : rows,
    visibleColumns: headers,
    hasFilters: filterCount > 0,
  };
}

function extractEntity(pageType: PageType): UIEntitySummary | undefined {
  if (pageType !== 'Patient360' && pageType !== 'CaseDetail') return undefined;

  const root = document.querySelector<HTMLElement>(
    '[data-entity-id], [data-patient-id], [data-case-id]',
  );
  const name =
    safeText(document.querySelector('[data-entity-name], [data-patient-name]')) ||
    safeText(document.querySelector('h1, [data-page-title]'));

  const cases = Number(
    document.querySelector('[data-entity-cases]')?.getAttribute('data-entity-cases') ?? NaN,
  );
  const approvedAmount =
    document.querySelector('[data-approved-amount]')?.getAttribute('data-approved-amount') ??
    safeText(document.querySelector('[data-kpi-key="approvedAmount"] [data-kpi-value]'));
  const utilizedAmount =
    document.querySelector('[data-utilized-amount]')?.getAttribute('data-utilized-amount') ??
    safeText(document.querySelector('[data-kpi-key="utilizedAmount"] [data-kpi-value]'));
  const status =
    safeText(document.querySelector('[data-entity-status], [data-status-badge]')) || undefined;

  return {
    id:
      root?.getAttribute('data-entity-id') ??
      root?.getAttribute('data-patient-id') ??
      root?.getAttribute('data-case-id') ??
      undefined,
    name: name || undefined,
    type: pageType === 'Patient360' ? 'PATIENT' : 'CASE',
    cases: Number.isFinite(cases) ? cases : undefined,
    approvedAmount: approvedAmount || undefined,
    utilizedAmount: utilizedAmount || undefined,
    status,
  };
}

function extractWorkflow(): UIWorkflowSummary {
  const pendingAttr = document.querySelector('[data-pending-tasks]')?.getAttribute(
    'data-pending-tasks',
  );
  const slaAttr = document.querySelector('[data-sla-breached]')?.getAttribute('data-sla-breached');
  const stepName =
    safeText(document.querySelector('[data-current-step]')) ||
    safeText(document.querySelector('[data-workflow-step]')) ||
    undefined;

  return {
    currentStep: stepName,
    pendingTasks: Number(pendingAttr ?? 0) || 0,
    slaBreached: Number(slaAttr ?? 0) || 0,
  };
}

function extractAlerts(): string[] {
  return Array.from(document.querySelectorAll<HTMLElement>('[data-alert], [role="alert"]'))
    .map((el) => safeText(el))
    .filter(Boolean)
    .slice(0, 5);
}

function extractFilters(): string[] {
  return Array.from(
    document.querySelectorAll<HTMLElement>('[data-active-filter], [data-filter-chip]'),
  )
    .map((el) => safeText(el))
    .filter(Boolean);
}

function extractVisibleSections(): string[] {
  return Array.from(document.querySelectorAll<HTMLElement>('[data-section-name]'))
    .map((el) => el.dataset.sectionName ?? '')
    .filter(Boolean);
}

// ──────────────────────────────────────────────────────────────────────
// Public API
// ──────────────────────────────────────────────────────────────────────

export interface ExtractContextOptions {
  pathname?: string;
  role?: string;
}

export function extractUIContext(opts: ExtractContextOptions = {}): UIContext {
  const pathname = opts.pathname ?? window.location.pathname;
  const { pageType, title } = classifyPage(pathname);

  const programId = extractIdFromPath(pathname, 'programs');
  const patientId = extractIdFromPath(pathname, 'patients');
  const caseId = extractIdFromPath(pathname, 'cases') ?? extractIdFromPath(pathname, 'case');

  const kpis = extractKpis();
  const tableSummary = extractTableSummary();
  const entity = extractEntity(pageType);
  const workflow = extractWorkflow();
  const alerts = extractAlerts();
  const filters = extractFilters();
  const visibleSections = extractVisibleSections();

  return {
    pageType,
    title,
    pathname,
    programId,
    patientId,
    caseId,
    role: opts.role,
    kpis,
    tableSummary,
    entity,
    workflow,
    pendingTasks: workflow.pendingTasks,
    alerts,
    visibleSections,
    filters,
  };
}

/** Convenience: find a KPI by key (case-insensitive substring on key/label). */
export function findKpi(ctx: UIContext, needle: string): UIKpi | undefined {
  const n = needle.toLowerCase();
  return ctx.kpis.find(
    (k) => k.key.toLowerCase().includes(n) || k.label.toLowerCase().includes(n),
  );
}

/** Convenience: numeric KPI value (returns 0 if missing/non-numeric). */
export function kpiValue(ctx: UIContext, needle: string): number {
  const k = findKpi(ctx, needle);
  if (!k) return 0;
  return typeof k.value === 'number' ? k.value : Number(String(k.value).replace(/[^\d.-]/g, '')) || 0;
}
